import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { DateField } from 'components/Field/DateField';
import { HistoryField } from 'components/Field/HistoryField';
import { ImageField } from 'componentsAware/ImageField';
import { ReviewStatusField } from 'components/Field/ReviewStatusField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  TextField,
  TextFieldRef,
  TextFieldSpecial,
} from 'components/Field/TextField';
import { TimescaleField } from 'components/Field/TimescaleField';
import { ICONS } from 'components/Icon';
import {
  ModalScreenProps,
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { addMonths, format } from 'date-fns';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { API_DATE_FORMAT } from 'lib/constants';
import { theme } from 'lib/theme';
import { animateNext } from 'lib/animateNext';
import { cloneDeep, isEqual, pick, sortBy, startCase } from 'lodash';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  eventAseSelectFindingQuestion,
  eventPinSelected,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/app/actions';
import {
  doAseCreateAction,
  doAseDeleteAction,
  doAseLoadPlans,
  doAseOpenFindingLocationModal,
  doAseUpdateAction,
} from 'store/assessments/actions';
import {
  selectAseCategories,
  selectAseDueTypes,
  selectAseTimescales,
  selectAseAccounts,
  selectAseAssetId,
  selectAseComplianceType,
  selectAseSeverities,
  selectAseTeams,
  selectAseTaskStatuses,
  selectAseTplSections,
  selectAseResultQuestion,
  selectAseTplQuestions,
  selectAseAssetTeamResponsibility,
  selectAseIsOnline,
  selectAseId,
} from 'store/assessments/selectors';
import { AssessmentItemTypes, Action } from 'store/assessments/types';
import { returnDefaultAction } from 'store/assessments/templates';
import {
  DueDeterminedBy,
  FilesContextEnum,
  TimescaleArray,
  TimescalePeriod,
} from 'types/types';
import { useEsc } from 'hooks/useEsc';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  filterArrayEmpty,
  randomKey,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { Txt } from 'components/Txt';
import { MiniPlanAssessment } from 'screens/FieldComponents/MiniPlanAssessment';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_ACTION
>;

export const ActionEdit: React.FC<
  | ModalScreenProps<ModalScreens.AseAction>
  | {
      route: Pick<Props['route'], 'params'>;
      navigation: {
        goBack: Props['navigation']['goBack'];
        setOptions: Props['navigation']['setOptions'];
      };
      isModal?: undefined;
    }
> = ({ route, navigation, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const { id, isDuplicate, parent, data: incomingData } = route.params;

  const isOnline = useSelector(selectAseIsOnline);
  const assessmentId = useSelector(selectAseId);

  const eventKeySelectCategory = React.useRef(randomKey());
  const eventKeySelectLocation = React.useRef(randomKey());
  const fieldKeyLocation = React.useRef(0);
  const eventKeySelectPeople = React.useRef(randomKey());
  const eventKeySelectTeams = React.useRef(randomKey());

  const eventKeyPinRef = React.useRef(randomKey());
  const costFieldRef = React.useRef<TextFieldRef>(null);

  const dataRef = React.useRef<Action>(cloneDeep(incomingData));
  const internalSave = !!isModal;

  const { pin } = dataRef.current;

  const isNew = !id;
  const action: Action = dataRef.current;

  const [currentlyEditing, setCurrentlyEditingActual] = React.useState<
    undefined | 'severity' | 'timescale' | 'due'
  >();

  const assetId = useSelector(selectAseAssetId, () => true);
  const teamResponsibility = useSelector(
    selectAseAssetTeamResponsibility,
    () => true,
  );
  const accounts = useSelector(selectAseAccounts);
  const teams = useSelector(selectAseTeams);
  const complianceType = useSelector(selectAseComplianceType);
  const templateQuestions = useSelector(selectAseTplQuestions);
  const templateSections = useSelector(selectAseTplSections);
  const taskStatuses = useSelector(selectAseTaskStatuses);
  const severities = useSelector(selectAseSeverities);
  const timescales = useSelector(selectAseTimescales);
  const categories = useSelector(selectAseCategories);
  const dueTypes = useSelector(selectAseDueTypes);

  const answerId = useSelector((state) =>
    selectAseResultQuestion(
      state,
      action.questionId || 'na',
      action.fieldInstanceId,
    ),
  )?.answerId;

  const category = categories[action.categoryId] || categories['0']!;
  const timescale = timescales[action.timescaleId] || timescales['0']!;
  const severity = severities[action.severityId] || severities['0']!;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(incomingData, dataRef.current),
    [incomingData],
  );
  isDirtyRef.current = checkIsDirty();

  const automaticallyAssignedTeamIds =
    category.responsible_team_ids[`10-${complianceType.id}-0`] ||
    category.responsible_team_ids['10-0-0'] ||
    category.responsible_team_ids['0-0-0'] ||
    [];

  const priorityBasedDue = Array.isArray(timescale.due)
    ? `${timescale.due[0]} ${startCase(timescale.due[1])}${
        timescale.due[0] > 1 ? 's' : ''
      }`
    : dueTypes[timescale.due]?.name || 'Unknown';

  const setCurrentlyEditing = (
    editing: Parameters<typeof setCurrentlyEditingActual>[0],
  ) => {
    if (editing) {
      animateNext();
    }
    setCurrentlyEditingActual(editing);
  };

  const handleHardSave = React.useCallback(() => {
    if (!id) {
      dispatch(doAseCreateAction({ data: dataRef.current }));
    } else if (checkIsDirty()) {
      dispatch(doAseUpdateAction({ id, data: dataRef.current }));
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkIsDirty, dispatch, id, isModal, navigation, screenSize]);

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleHardSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<Action>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = { ...dataRef.current, ...changes };
        if (
          doForceUpdate === 'always' ||
          (internalSave &&
            doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        }
        if (!internalSave) {
          dispatch(doAseUpdateAction({ id, data: newValues }));
        }
      }
    },
    [checkIsDirty, dispatch, forceUpdate, id, internalSave],
  );

  const saveField = React.useCallback(
    (name: keyof Action, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  const saveReviewStatus = React.useCallback(
    (reviewStatusId: string) =>
      handleSoftSave({ reviewStatusId }, { doForceUpdate: 'always' }),
    [handleSoftSave],
  );

  const handleImageChange = React.useCallback(
    (value: string[]) =>
      handleSoftSave(
        {
          photoIds: value,
        },
        { doForceUpdate: 'always' },
      ),
    [handleSoftSave],
  );

  const handleDelete = () =>
    confirm({
      title: action.isReview ? 'Delete Review' : 'Delete Action',
      text: action.isReview
        ? 'Are you sure you want to delete the review for this action?'
        : 'Are you sure you want to delete this action?',
      confirmText: 'Delete',
      onConfirm: () => dispatch(doAseDeleteAction({ id: action.id })),
      destructive: true,
    });

  const handleCategoryPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Category',
          eventKey: eventKeySelectCategory.current,
          value: action.categoryId ? [action.categoryId] : undefined,
          options: sortBy(
            Object.values(categories).map((cat) => ({
              name: cat.name,
              groupName: cat.group_name,
              internalValue: cat.id,
              ordering:
                (cat.group_name === 'Uncategorised' ? 0 : cat.group_name) +
                cat.name,
            })),
            ['ordering'],
          ),
        },
      }),
    );

  const handleSuggestTemplate = () =>
    !action.questionId
      ? dispatch(
          ALERT({
            text:
              'Actions cannot be suggested for sections. ' +
              'Move this action to a question or checklist before turning it into a suggestion',
          }),
        )
      : dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseMakeSuggestion,
            params: {
              type: 'action',
              description: action.description,
              why: action.why,
              categoryId: category.id,
              severityId: severity.id,
              timescaleId: timescale.id,
              dueDeterminedBy: action.dueDeterminedBy,
              dueTimescale: action.dueTimescale,
              dueTypeId: action.dueTypeId || '',
              questionId: action.questionId,
              answerId: answerId || undefined,
              cost: action.cost,
            },
          }),
        );

  const handleOpenPeople = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'People',
          value: action.assignedAccountIds,
          eventKey: eventKeySelectPeople.current,
          multiSelect: true,
          searchable: true,
          options: naturalOrderBy(
            Object.values(accounts).map((account) => ({
              internalValue: account.id,
              name: account.name,
            })),
            'name',
          ),
        },
      }),
    );

  const handleOpenTeams = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Teams',
          value: action.assignedTeamIds,
          eventKey: eventKeySelectTeams.current,
          multiSelect: true,
          searchable: true,
          options: naturalOrderBy(
            Object.values(teams).map((team) => ({
              name: team.name,
              internalValue: team.id,
            })),
            'name',
          ),
        },
      }),
    );

  const handleSetTimescale = (newValue: string) => {
    handleSoftSave({ timescaleId: newValue }, {});
    setCurrentlyEditing(undefined);
  };

  const handleSetSeverity = (newValue: string) => {
    handleSoftSave({ severityId: newValue }, {});
    setCurrentlyEditing(undefined);
  };

  const handleSetDueDate = (newValue: string) => {
    if (action.dueDate !== newValue) {
      handleSoftSave({ dueDate: newValue }, { doForceUpdate: 'dirtyChange' });
    }
  };

  const handleSetDueTimescale = (newTimescale: TimescaleArray) => {
    if (
      !action.dueTimescale ||
      action.dueTimescale[0] !== newTimescale[0] ||
      action.dueTimescale[1] !== newTimescale[1]
    ) {
      handleSoftSave(
        {
          dueTimescale: newTimescale,
        },
        { doForceUpdate: 'always' },
      );
    }
  };

  const handleSetDue = (newCat: DueDeterminedBy, newValue: string) => {
    if (newCat === DueDeterminedBy.timescaleBased) {
      if (action.dueDeterminedBy !== DueDeterminedBy.timescaleBased) {
        handleSoftSave(
          {
            dueDeterminedBy: DueDeterminedBy.timescaleBased,
            dueDate: null,
            dueTimescale: null,
            dueTypeId: null,
          },
          { doForceUpdate: 'always' },
        );
      }
    } else if (newCat === DueDeterminedBy.timescale) {
      const dueTimescale = action.dueTimescale || [1, TimescalePeriod.year];
      if (
        action.dueDeterminedBy !== DueDeterminedBy.timescaleBased ||
        !action.dueTimescale ||
        action.dueTimescale[0] !== dueTimescale[0] ||
        action.dueTimescale[1] !== dueTimescale[1]
      ) {
        handleSoftSave(
          {
            dueDeterminedBy: DueDeterminedBy.timescale,
            dueTimescale: action.dueTimescale || [1, TimescalePeriod.month],
            dueDate: null,
            dueTypeId: null,
          },
          { doForceUpdate: 'always' },
        );
      }
    } else {
      const dueType = dueTypes[newValue]!;
      const dueDate = dueType.date
        ? action.dueDate || format(addMonths(new Date(), 1), API_DATE_FORMAT)
        : null;
      if (
        action.dueDeterminedBy !== DueDeterminedBy.type ||
        action.dueTypeId !== newValue ||
        action.dueDate !== dueDate
      ) {
        handleSoftSave(
          {
            dueDeterminedBy: DueDeterminedBy.type,
            dueDate,
            dueTypeId: newValue,
            dueTimescale: null,
          },
          { doForceUpdate: 'always' },
        );
      }
    }
    setCurrentlyEditing(undefined);
  };

  const handleDuplicatePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseAction,
        params: {
          id: '',
          isDuplicate: true,
          parent,
          data: {
            ...returnDefaultAction({
              questionId: action.questionId,
              sectionId: action.sectionId,
              fieldInstanceId: action.fieldInstanceId,
              suggestionId: action.suggestionId,
            }),
            ...pick(action, [
              'description',
              'why',
              'where',
              'categoryId',
              'timescaleId',
              'severityId',
              'dueDate',
              'dueDeterminedBy',
              'dueTimescale',
              'dueTypeId',
              'assignedAccountIds',
              'assignedTeamIds',
              'quantity',
              'cost',
            ]),
          },
        },
      }),
    );

  const selectQuestionEventKey = React.useRef(randomKey()).current;
  const handleMovePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASEFINDINGBELONGSTO,
        params: {
          findingType: AssessmentItemTypes.ACTION,
          questionId: action.questionId,
          sectionId: action.sectionId,
          fieldInstanceId: action.fieldInstanceId,
          eventKey: selectQuestionEventKey,
        },
      }),
    );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventPinSelected, (event) => {
        if (event.payload.key === eventKeyPinRef.current) {
          handleSoftSave(
            {
              pin: event.payload.data
                ? {
                    planId: `${event.payload.data.planId}`,
                    x: `${event.payload.data.x}`,
                    y: `${event.payload.data.y}`,
                  }
                : null,
            },
            { doForceUpdate: 'always' },
          );
        }
      }),
      doAddEvent(eventAseSelectFindingQuestion, (event) => {
        if (event.payload.key === selectQuestionEventKey) {
          handleSoftSave(event.payload.data, { doForceUpdate: 'always' });
        }
      }),
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeySelectCategory.current) {
          handleSoftSave(
            {
              categoryId: event.payload.stringValues[0],
            },
            { doForceUpdate: 'always' },
          );
        } else if (event.payload.key === eventKeySelectLocation.current) {
          fieldKeyLocation.current += 1;
          handleSoftSave(
            {
              where: event.payload.stringValues[0] || '',
            },
            { doForceUpdate: 'always' },
          );
        } else if (event.payload.key === eventKeySelectPeople.current) {
          handleSoftSave(
            {
              assignedAccountIds: event.payload.stringValues,
            },
            { doForceUpdate: 'always' },
          );
        } else if (event.payload.key === eventKeySelectTeams.current) {
          handleSoftSave(
            {
              assignedTeamIds: event.payload.stringValues,
            },
            { doForceUpdate: 'always' },
          );
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate, handleSoftSave, selectQuestionEventKey]);

  const DueField =
    currentlyEditing !== 'due' ? (
      <ViewWithSeparators
        horizontal
        style={{ backgroundColor: theme.colors.white }}
      >
        <CardRow
          label="Due"
          value={
            action.dueDeterminedBy === DueDeterminedBy.timescaleBased
              ? `Based on Timescale: ${priorityBasedDue}`
              : action.dueDeterminedBy === DueDeterminedBy.timescale
                ? 'Custom Timescale'
                : action.dueDate
                  ? 'Custom Date'
                  : dueTypes[action.dueTypeId || -1]?.name || 'Unknown'
          }
          onPress={() => setCurrentlyEditing('due')}
          style={{ flex: 1 }}
          icon={ICONS.CalendarDayLight}
        />
        {!!action.dueDate && (
          <DateField
            value={action.dueDate}
            onChange={handleSetDueDate}
            clearable={false}
            showFieldTypeIcon={false}
            shrinkToWidth
          />
        )}
        {action.dueDeterminedBy === DueDeterminedBy.timescale &&
          !!action.dueTimescale && (
            <CardRow
              style={{
                backgroundColor: 'white',
                width: 144,
              }}
              contentStyle={{
                paddingVertical: 0,
              }}
            >
              <TimescaleField
                onChange={handleSetDueTimescale}
                timescale={action.dueTimescale}
              />
            </CardRow>
          )}
      </ViewWithSeparators>
    ) : (
      <Card expandedInline margin={false} marginHorizontal={false}>
        {[
          {
            name: `Based on Timescale: ${priorityBasedDue}`,
            id: DueDeterminedBy.timescaleBased,
            id2: DueDeterminedBy.timescaleBased,
            id3: null,
            selected: action.dueDeterminedBy === DueDeterminedBy.timescaleBased,
          },
          {
            name: 'Custom Timescale',
            id: DueDeterminedBy.timescale,
            id2: DueDeterminedBy.timescale,
            id3: null,
            selected: action.dueDeterminedBy === DueDeterminedBy.timescale,
          },
          ...Object.values(dueTypes).map((dueType) => ({
            name: dueType.name,
            id: DueDeterminedBy.type,
            id2: dueType.id,
            id3: dueType.id,
            onPress: () => {},
            selected: action.dueTypeId === dueType.id,
          })),
        ].map((p) => (
          <CardRow
            key={p.id2}
            label={p.name}
            radioValue={
              action.dueDeterminedBy === p.id && action.dueTypeId === p.id3
            }
            onPress={() => handleSetDue(p.id, p.id2)}
          />
        ))}
      </Card>
    );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleClose);

  return (
    <PageScrollView
      isInModal={!!isModal}
      header={
        <NavigationHeader
          onPressSaveButton={isModal ? handleHardSave : undefined}
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
          heading={`${isDuplicate ? 'Duplicate ' : isNew ? 'New ' : ''}Action`}
          menuRight={
            isNew
              ? undefined
              : {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    {
                      text: 'Move',
                      icon: ICONS.HandLight,
                      onPress: handleMovePress,
                    },
                    {
                      text: 'Duplicate',
                      icon: ICONS.CloneLight,
                      onPress: handleDuplicatePress,
                    },
                    isOnline
                      ? null
                      : {
                          text: 'Suggest',
                          icon: ICONS.LightbulbOnLight,
                          onPress: handleSuggestTemplate,
                        },
                    '-',
                    {
                      text: action.isReview ? 'Delete Review' : 'Delete Action',
                      icon: ICONS.TrashCanLight,
                      destructive: true,
                      onPress: handleDelete,
                    },
                  ].filter(filterArrayEmpty),
                }
          }
        />
      }
    >
      {!isNew && (
        <Card>
          <ViewWithSeparators horizontal style={{ flex: 1 }}>
            <CardRow
              icon={ICONS.RBPlectrumSolid}
              label={
                action.reference
                  ? rbAddReferencePrefix(action.reference)
                  : undefined
              }
              label2={action.customReference}
              style={{ flex: 1 }}
            >
              {!action.reference && (
                <Txt>
                  {rbAddReferencePrefix('')}
                  <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
                </Txt>
              )}
            </CardRow>
            <HistoryField
              history={action.history}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.ASEHISTORY,
                    params: {
                      id,
                      findingType: AssessmentItemTypes.ACTION,
                    },
                  }),
                )
              }
            />
          </ViewWithSeparators>
        </Card>
      )}

      {!!action.reviewStatusId && (
        <Card>
          <ReviewStatusField
            options={taskStatuses.map((status) => ({
              label: status.name,
              value: status.id,
            }))}
            value={action.reviewStatusId}
            onChange={saveReviewStatus}
          />
          <TextAreaField
            placeholder="Review Note"
            value={action.reviewNote}
            name="reviewNote"
            onChangeWithName={saveField}
            onEsc={handleClose}
          />
        </Card>
      )}

      <Card margin={false}>
        <TextAreaField
          placeholder="What is the action? (required)"
          value={action.description}
          name="description"
          onChangeWithName={saveField}
          onEsc={handleClose}
          autoFocusWeb={isModal}
          autoFocusDelay
        />
        <TextAreaField
          placeholder="Why is it needed?"
          value={action.why}
          name="why"
          onChangeWithName={saveField}
          onEsc={handleClose}
        />
        <TextField
          placeholder="Location"
          key={`location-${fieldKeyLocation.current}`}
          value={action.where}
          name="where"
          onChangeWithName={saveField}
          showTextIcon
          onEsc={handleClose}
        />
      </Card>

      <View
        style={{
          marginBottom: theme.paddingVertical,
          paddingHorizontal: theme.paddingHorizontal,
        }}
      >
        <Button
          uppercase={false}
          text="Location Suggestions"
          buttonTheme={ButtonTheme.link}
          onPress={() =>
            dispatch(
              doAseOpenFindingLocationModal({
                eventKey: eventKeySelectLocation.current,
                value: action.where,
              }),
            )
          }
          textWeight="regular"
          style={{ marginTop: 6, alignSelf: 'flex-start' }}
        />
      </View>

      <Card>
        {/** Category */}
        {Object.values(categories).length > 1 && (
          <CardRow
            label="Category"
            value={category.name}
            onPress={handleCategoryPress}
            icon={ICONS.TagLight}
          />
        )}
        {/** Severity */}
        {Object.keys(severities).length > 1 &&
          (currentlyEditing !== 'severity' ? (
            <CardRow
              label="Severity"
              value={severity.name}
              onPress={() => setCurrentlyEditing('severity')}
              icon={ICONS.DiamondExclamationSolid}
            />
          ) : (
            <Card
              expandedInline
              margin={false}
              marginHorizontal={false}
              expandedInlineFirst={Object.values(categories).length < 2}
            >
              {Object.values(severities)
                .sort((a, b) => a.score - b.score)
                .map((p) => (
                  <CardRow
                    key={p.id}
                    label={p.name}
                    radioValue={severity.id === p.id}
                    onPress={() => handleSetSeverity(p.id)}
                  />
                ))}
            </Card>
          ))}
        {/** Timescale */}
        {Object.keys(timescales).length > 1 &&
          (currentlyEditing !== 'timescale' ? (
            <CardRow
              label="Timescale"
              value={timescale.name}
              onPress={() => setCurrentlyEditing('timescale')}
              style={{ flex: 1 }}
              icon={ICONS.HourglassLight}
            />
          ) : (
            <Card
              expandedInline
              margin={false}
              marginHorizontal={false}
              expandedInlineFirst={
                Object.values(categories).length < 2 &&
                Object.keys(severities).length < 2
              }
            >
              {Object.values(timescales)
                .sort((a, b) => a.score - b.score)
                .map((p) => (
                  <CardRow
                    key={p.id}
                    label={p.name}
                    radioValue={timescale.id === p.id}
                    onPress={() => handleSetTimescale(p.id)}
                  />
                ))}
            </Card>
          ))}
        {/** Due */}
        {DueField}
        <CardRow
          label="Estimated Cost"
          onPress={() => costFieldRef.current?.focus()}
          contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
          icon={ICONS.SterlingSignLight}
        >
          <TextField
            myRef={costFieldRef}
            placeholder="No Cost"
            value={action.cost !== undefined ? action.cost : ''}
            name="cost"
            onChangeWithName={saveField}
            special={TextFieldSpecial.currency}
            onEsc={handleClose}
            containerStyle={{ backgroundColor: 'transparent', width: 120 }}
            inputStyle={{ textAlign: 'right', paddingRight: 0 }}
          />
        </CardRow>
        {!pin ? (
          <CardRow
            icon={ICONS.LocationPinLight}
            label="Pin Location"
            placeholder="No Pin"
            onPress={() =>
              dispatch(
                doAseLoadPlans({
                  pin: undefined,
                  pins: [],
                  selectionKey: eventKeyPinRef.current,
                }),
              )
            }
          />
        ) : (
          <CardRow
            icon={ICONS.LocationPinLight}
            label="Pin Location"
            placeholder="No Pin"
            onPress={() =>
              dispatch(
                doAseLoadPlans({
                  pin: pin
                    ? {
                        planId: pin.planId,
                        x: parseFloat(pin.x),
                        y: parseFloat(pin.y),
                      }
                    : undefined,
                  pins: [],
                  selectionKey: eventKeyPinRef.current,
                }),
              )
            }
          >
            <MiniPlanAssessment
              planId={pin.planId}
              x={parseFloat(pin.x)}
              y={parseFloat(pin.y)}
              maxHeight={100}
              maxWidth={100}
            />
          </CardRow>
        )}
      </Card>

      <Card>
        <ImageField
          context={
            isOnline
              ? {
                  context: FilesContextEnum.remoteAssessment,
                  assetId,
                  assessmentId,
                }
              : {
                  context: FilesContextEnum.localAssessment,
                  assetId,
                  assessmentId,
                }
          }
          value={action.photoIds}
          onChange={handleImageChange}
          promptMessage="Add to Action"
          multiSelect
        />
      </Card>

      <Card>
        <CardRow
          labelAlignTop
          label="Teams Automatically Assigned"
          value={
            naturalOrderBy(
              automaticallyAssignedTeamIds
                .filter((teamId) => !!teams[teamId])
                .map((teamId) => {
                  const responsible = naturalOrderBy(
                    (teamResponsibility[teamId] || []).reduce<string[]>(
                      (acc, accountId) => {
                        const account = accounts[accountId];
                        if (account) {
                          acc.push(account.name);
                        }
                        return acc;
                      },
                      [],
                    ),
                  ).join(', ');
                  return (
                    teams[teamId]!.name +
                    (responsible ? ` (${responsible})` : '')
                  );
                }),
            ).join(', ') || 'No Teams'
          }
        />
        <CardRow
          onPress={handleOpenTeams}
          label="Teams Manually Assigned"
          labelAlignTop
          value={
            naturalOrderBy(
              action.assignedTeamIds
                .filter((teamId) => !!teams[teamId])
                .map((teamId) => {
                  const responsible = naturalOrderBy(
                    (teamResponsibility[teamId] || []).reduce<string[]>(
                      (acc, accountId) => {
                        const account = accounts[accountId];
                        if (account) {
                          acc.push(account.name);
                        }
                        return acc;
                      },
                      [],
                    ),
                  ).join(', ');
                  return (
                    teams[teamId]!.name +
                    (responsible ? ` (${responsible})` : '')
                  );
                }),
            ).join(', ') || 'No Teams'
          }
        />
        <CardRow
          onPress={handleOpenPeople}
          labelAlignTop
          label="People Manually Assigned"
          value={
            naturalOrderBy(
              action.assignedAccountIds.reduce<string[]>((acc, accountId) => {
                const account = accounts[accountId];
                if (account) {
                  acc.push(account.name);
                }
                return acc;
              }, []),
            ).join(', ') || 'Nobody'
          }
        />
      </Card>

      <Card>
        <CardRow
          label="Belongs To"
          labelAlignTop
          value={
            action.questionId !== null
              ? templateQuestions[action.questionId]?.question
              : action.sectionId
                ? templateSections[action.sectionId]?.name
                : ''
          }
          onPress={handleMovePress}
        />
      </Card>
    </PageScrollView>
  );
};
